import React, { useState, useEffect } from 'react';
import API from '../utils/API';

export default function AdminApprovedDomains() {
    const [domains, setDomains] = useState([]);
    const [loading, setLoading] = useState(true);

    const [editingId, setEditingId] = useState(null);
    const [editDraft, setEditDraft] = useState('');
    const [editError, setEditError] = useState('');
    const [saving, setSaving] = useState({});

    const [confirmDeleteId, setConfirmDeleteId] = useState(null);

    const [newDomain, setNewDomain] = useState('');
    const [addError, setAddError] = useState('');
    const [adding, setAdding] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const res = await API.getApprovedDomains();
            setDomains(res.data);
        } catch (err) {
            console.error('Failed to load approved domains:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const handleAdd = async (e) => {
        e.preventDefault();
        const domain = newDomain.trim().toLowerCase();
        if (!domain) return;
        setAdding(true);
        setAddError('');
        try {
            await API.addApprovedDomain({ approved_domain: domain });
            setNewDomain('');
            await load();
        } catch (err) {
            setAddError(err.response?.data?.msg || 'Failed to add domain.');
        } finally {
            setAdding(false);
        }
    };

    const startEdit = (domain) => {
        setEditingId(domain.domain_id);
        setEditDraft(domain.approved_domain);
        setEditError('');
        setConfirmDeleteId(null);
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditDraft('');
        setEditError('');
    };

    const saveEdit = async (domain_id) => {
        const domain = editDraft.trim().toLowerCase();
        if (!domain) return;
        setSaving(s => ({ ...s, [domain_id]: true }));
        setEditError('');
        try {
            await API.updateApprovedDomain(domain_id, { approved_domain: domain });
            cancelEdit();
            await load();
        } catch (err) {
            setEditError(err.response?.data?.msg || 'Failed to save.');
        } finally {
            setSaving(s => ({ ...s, [domain_id]: false }));
        }
    };

    const confirmDelete = async (domain_id) => {
        setSaving(s => ({ ...s, [`del_${domain_id}`]: true }));
        try {
            await API.deleteApprovedDomain(domain_id);
            setConfirmDeleteId(null);
            setDomains(prev => prev.filter(d => d.domain_id !== domain_id));
        } catch (err) {
            console.error('Failed to delete approved domain:', err);
        } finally {
            setSaving(s => ({ ...s, [`del_${domain_id}`]: false }));
        }
    };

    return (
        <div>
            <p className="text-muted small mb-3">
                Anyone registering with an email from one of these domains is automatically eligible to create an account.
            </p>

            <form className="ai-add-row" onSubmit={handleAdd}>
                <input
                    className="admin-search-input"
                    placeholder="example.com"
                    value={newDomain}
                    onChange={e => { setNewDomain(e.target.value); setAddError(''); }}
                />
                <button className="awards-save-btn" type="submit" disabled={adding || !newDomain.trim()}>
                    {adding ? <i className="bi bi-arrow-repeat admin-spin" /> : 'Add'}
                </button>
            </form>
            {addError && <div className="ai-error mb-2">{addError}</div>}

            {loading ? (
                <div className="admin-loading">Loading…</div>
            ) : (
                <div className="admin-table-wrapper">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>Domain</th>
                                <th className="admin-th-center">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {domains.length === 0 ? (
                                <tr><td colSpan={2} className="admin-empty">No approved domains found</td></tr>
                            ) : domains.map(domain => (
                                <tr key={domain.domain_id}>
                                    <td data-label="Domain">
                                        {editingId === domain.domain_id ? (
                                            <div className="admin-email-edit">
                                                <input
                                                    className="admin-email-input"
                                                    value={editDraft}
                                                    onChange={e => { setEditDraft(e.target.value); setEditError(''); }}
                                                    onKeyDown={e => {
                                                        if (e.key === 'Enter') saveEdit(domain.domain_id);
                                                        if (e.key === 'Escape') cancelEdit();
                                                    }}
                                                    autoFocus
                                                    style={{ width: '100%', maxWidth: 260 }}
                                                />
                                                <div className="admin-email-actions">
                                                    <button
                                                        className="admin-email-save"
                                                        onClick={() => saveEdit(domain.domain_id)}
                                                        disabled={saving[domain.domain_id]}
                                                        title="Save"
                                                    >
                                                        {saving[domain.domain_id]
                                                            ? <i className="bi bi-arrow-repeat admin-spin" />
                                                            : <i className="bi bi-check-lg" />}
                                                    </button>
                                                    <button className="admin-email-cancel" onClick={cancelEdit} title="Cancel">
                                                        <i className="bi bi-x-lg" />
                                                    </button>
                                                </div>
                                                {editError && <div className="admin-email-error">{editError}</div>}
                                            </div>
                                        ) : (
                                            <span className="admin-name">{domain.approved_domain}</span>
                                        )}
                                    </td>
                                    <td className="admin-td-center" data-label="Actions">
                                        {confirmDeleteId === domain.domain_id ? (
                                            <div className="ai-delete-confirm">
                                                <span className="ai-delete-confirm-msg">Delete this domain?</span>
                                                <button
                                                    className="awards-btn awards-btn--delete ai-action-btn"
                                                    onClick={() => confirmDelete(domain.domain_id)}
                                                    disabled={saving[`del_${domain.domain_id}`]}
                                                >
                                                    {saving[`del_${domain.domain_id}`]
                                                        ? <i className="bi bi-arrow-repeat admin-spin" />
                                                        : 'Yes, delete'}
                                                </button>
                                                <button
                                                    className="awards-btn ai-action-btn"
                                                    onClick={() => setConfirmDeleteId(null)}
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="ai-action-group">
                                                <button
                                                    className="awards-btn awards-btn--edit ai-action-btn"
                                                    onClick={() => startEdit(domain)}
                                                    title="Edit"
                                                >
                                                    <i className="bi bi-pencil me-1" />Edit
                                                </button>
                                                <button
                                                    className="awards-btn awards-btn--delete ai-action-btn"
                                                    onClick={() => { setConfirmDeleteId(domain.domain_id); setEditingId(null); }}
                                                    title="Delete"
                                                >
                                                    <i className="bi bi-trash me-1" />Delete
                                                </button>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
